import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { FeatureSelectService, MapTableService } from '@ebw-monorepo/map';
import { EbwModalService } from '@ebw-monorepo/modal';
import { SplitViewService, UtilsService } from '@ebw-monorepo/utils';
import { IMapTable } from 'libs/ng-components/map/src/lib/interfaces/mapTable.interface';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { TelcoService } from 'libs/ng-components/map/src/lib/services/telco.service';
import { SplitViewConf } from 'libs/ng-components/utils/src/lib/models/split-view-conf';
import { Subject, Subscription, debounceTime, filter, lastValueFrom, switchMap, takeUntil } from 'rxjs';
import { OtdrTableComponent } from '../../../../otdr-table/otdr-table.component';
import { OTDRButton } from '../../model/otdr.model';

@Component({
  selector: 'ebw-monorepo-fastweb-otdr-form',
  templateUrl: './fastweb-otdr-form.component.html',
  styleUrls: ['./fastweb-otdr-form.component.scss']
})
export class FastwebOtdrFormComponent implements OnInit, OnDestroy {
  estremiFibra: { [key: string]: string }[] = [];
  searchResultsObject: any[] = [];

  form: FormGroup;
  buttons: OTDRButton[] = [
    { label: 'Avvia', type: 'flat' },
    { label: 'Pulisci', type: 'outline' }
  ];

  destroy$: Subject<void> = new Subject<void>();
  responseError: boolean;
  private _contextId = 'otdr_table';
  // string storing the urn of the object coming from the detail OTDR
  private _objectUrn: string = null;
  autocompleteSubscription: Subscription;
  // variable to keep track of whether the input on the search is from the autocomplete or not
  autocompleted = false;

  // ------------------------------------------------------------------------------------------------

  constructor(
    private _ebwModalService: EbwModalService,
    private _fb: FormBuilder,
    private _fsService: FeatureSelectService,
    private _i18NService: I18NService,
    private _mapTableService: MapTableService,
    private _splitViewService: SplitViewService,
    private _utilsService: UtilsService,
    private _telcoService: TelcoService,
    private _apiUrlService: ApiUrlService
  ) {}

  ngOnInit(): void {
    this.initForm();
    // quando viene chiamata apertura pannello OTDR controllo se ci sono parametri in ingresso
    // se è così, l'apertura dell'OTDR è stata triggerata dal dettaglio di un asset
    // se non c'è niente è stato aperto dal menu
    this._fsService.openOtdrPanel$
      .pipe(takeUntil(this.destroy$), filter(Boolean))
      .subscribe(() => this.takeRootParams());
  }

  // ------------------------------------------------------------------------------------------------

  private takeRootParams() {
    const { urn, name } = history.state;
    if (urn && name) {
      this.initForm();
      this.form.patchValue({ object: name });
      this.getEstremiOtdrFromSegmentoOttico(urn);
      // store the urn in the local variable
      this._objectUrn = urn;
    }
  }

  // ------------------------------------------------------------------------------------------------

  onChangeComponentHandler(value: string) {
    switch (value) {
      case 'Avvia':
        this.startOtdr();
        break;
      default:
        this.resetForm();
        break;
    }
  }

  // ------------------------------------------------------------------------------------------------

  startOtdr() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    // estraggo i valori dal form
    const { distance, device_urn, object } = this.form.value;
    // find the object urn
    // if it's already stored, use the stored one. Otherwise find it in the result of the search
    const objectUrn = this._objectUrn
      ? this._objectUrn
      : this.searchResultsObject.find((r) => r.desc?.replaceAll(', ', ',').includes(object.replaceAll(', ', ',')))
          ?.urns;

    if (!device_urn || !distance || !objectUrn) {
      const content = this._i18NService.getTranslateParameter('map.sidebar.otdr.FAS.invalid_error_option');
      const title = this._i18NService.getTranslateParameter('common.warning');
      this._ebwModalService.openWarningModal({ title, content });
      return;
    }

    const url = `${this._apiUrlService.createUrl(
      API_URLS.MAP.TELCO.OTDR_START
    )}?object=${objectUrn}&distance=${distance}&device_urn=${device_urn}`;
    // const url = 'https://ae7a4270-9a93-42b5-863d-0be93ce0601a.mock.pstmn.io/gss/start_otdr_new';
    const config: IMapTable = {
      url
    };
    const splitViewConf: SplitViewConf = {
      title: 'map.sidebar.otdr.title',
      icon: 'table_view'
    };
    this._splitViewService.show(OtdrTableComponent, this._contextId, null, splitViewConf);
    this._mapTableService.setMapTableConfig(config);
  }

  // ------------------------------------------------------------------------------------------------

  private setupAutocomplete(): void {
    if (this.autocompleteSubscription) {
      return;
    }
    // mi sottoscrivo ai cambiamenti dell'autocomplete per avviare la ricerca su solr
    this.autocompleteSubscription = this.form
      .get('object')
      .valueChanges.pipe(
        // tempo minimo tra una richiesta e l'altra
        debounceTime(300),
        // filtra gli eventi triggerati dall'autocomplete
        filter((value) => {
          if (this.autocompleted) {
            this.autocompleted = false;
            return false;
          }

          return true;
        }),
        // cerco solo con almeno 3 caratteri
        filter((value: string) => value?.length > 2),
        // ricerca solr
        switchMap((value: string) => this._telcoService.otdrSearch(value))
      )
      .subscribe({
        next: (results: any[]) => {
          // resetta il flag autocompleted
          this.autocompleted = false;
          // Se non ho trovato risultati
          if (!results.length) {
            this.searchResultsObject = [];
            return;
          }
          // altrimenti popolo le opzioni dell'autocomplete
          this.searchResultsObject = results;
        },
        error: (error) => {
          // resetta il flag autocompleted
          this.autocompleted = false;
          const { urn, name } = history.state;
          // setto eventuali errori e pulisco l'array
          console.error('[FastwebOtdrFormComponent][setupAutocomplete]', error);
          this.searchResultsObject = [];
          this.form.get('object').setErrors({ solrError: true });
          this.autocompleteSubscription.unsubscribe();
          this.setupAutocomplete();
        }
      });
  }

  // ------------------------------------------------------------------------------------------------

  async getEstremiOtdrFromSegmentoOttico(urn: string) {
    // resetta il flag autocompleted
    this.autocompleted = true;
    // resetta l'oggetto urn
    this._objectUrn = null;
    // const url = 'https://ae7a4270-9a93-42b5-863d-0be93ce0601a.mock.pstmn.io/gss/segmento';
    const url = `${this._apiUrlService.createUrl(API_URLS.MAP.TELCO.ESTREMI_OTDR_DA_SEGMENTO)}?object=${urn}`;
    const request = this._utilsService.getRequest(url, {});
    try {
      const response = await lastValueFrom(request);
      this._setEstremiFibra(response);
    } catch (error) {
      this.responseError = true;
      this._ebwModalService.openErrorModal({
        title: this._i18NService.getTranslateParameter('common.error'),
        content: 'Errore recupero estremi segmento ottico'
      });
    }
  }

  // ------------------------------------------------------------------------------------------------

  private _setEstremiFibra(results: { [key: string]: string }) {
    this.estremiFibra = [];
    // metodo utilizzato per popolare l'array, poiche' le chiavi dell'oggetto sono dinamiche e non si conoscono i nomi a priori
    for (const key in results) {
      if (Object.prototype.hasOwnProperty.call(results, key)) {
        this.estremiFibra.push({ [key]: results[key] });
      }
    }
  }

  // ------------------------------------------------------------------------------------------------

  private initForm() {
    // resetta l'oggetto urn
    this._objectUrn = null;
    if (this.form) {
      this.resetForm();
    } else {
      this.form = this._fb.group({
        object: ['', [this.checkIfOptionIsSelected.bind(this)]],
        device_urn: ['', [Validators.required]],
        distance: ['', [Validators.required]]
      });
    }
    this.setupAutocomplete();
  }

  // ------------------------------------------------------------------------------------------------

  private checkIfOptionIsSelected(control: any) {
    if (!this.searchResultsObject.length) {
      return null;
    }
    try {
      // controllo se il nome presente nel form corrisponde ad una delle opzioni ricercate
      const deviceUrnInvalid = !this.searchResultsObject.find((obj) => obj.desc === control.value);
      // se non lo è metto un errore sul campo input
      return deviceUrnInvalid ? { optionError: true } : null;
    } catch (error) {
      console.error('[FastwebOtdrFormComponent][checkIfOptionIsSelected]', error);
      return null;
    }
  }

  // ------------------------------------------------------------------------------------------------

  private resetForm(): void {
    // svuota le opzioni della possibile ricerca precedente
    this.searchResultsObject = [];
    // resetto i campi del form
    this.form.reset({ device_urn: '', object: '', distance: '' });
  }

  // ------------------------------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
